import { create } from 'zustand';
import { getTenantIdFromLocalStorage } from '../../../utils/tokenUtils';
import {
  getDismissedSpotlights,
  dismissSpotlight as cacheDismissSpotlight,
} from '../../../utils/spotlightsCache';
import type { Spotlight } from '../../../api/spotlights';
import {
  fetchSpotlights as apiFetchSpotlights,
  fetchSpotlightById,
} from '../../../api/spotlights';

interface SpotlightStore {
  spotlights: Spotlight[];
  loading: boolean;
  error: string | null;
  dismissed: string[];
  lastFetched: Date | null;
  fetchSpotlights: () => Promise<void>;
  dismiss: (id: string) => Promise<void>;
  getSpotlightByTypeId: (id: string) => Spotlight | undefined;
  getSpotlightById: (id: string) => Promise<Spotlight | undefined>;
  getFeatureSpotlight: () => Spotlight | undefined;
  clear: () => void;
}

export const useSpotlightStore = create<SpotlightStore>((set, get) => ({
  spotlights: [],
  loading: false,
  error: null,
  dismissed: [],
  lastFetched: null,

  fetchSpotlights: async () => {
    set({ loading: true, error: null });
    const tenantId = getTenantIdFromLocalStorage();
    if (!tenantId) {
      set({ loading: false, error: 'No tenantId' });
      return;
    }
    try {
      const data = await apiFetchSpotlights();
      const dismissedSpotlightTypes = await getDismissedSpotlights(
        tenantId,
        data.data.spotlights,
      );

      set({
        spotlights: data.data.spotlights.filter(
          (s) => !dismissedSpotlightTypes.includes(s.spotlightType),
        ),
        dismissed: dismissedSpotlightTypes,
        loading: false,
        error: null,
        lastFetched: new Date(),
      });
    } catch (e) {
      set({ loading: false, error: String(e) });
    }
  },

  // Dismiss a spotlight by id, update state and cache
  dismiss: async (id) => {
    const tenantId = getTenantIdFromLocalStorage();
    if (!tenantId) {
      return;
    }
    await cacheDismissSpotlight(tenantId, id);
    set((state) => ({
      dismissed: [...state.dismissed, id],
      spotlights: state.spotlights.filter((s) => s.spotlightType !== id),
    }));
  },

  // Get a spotlight by id
  getSpotlightByTypeId: (id) => {
    const { spotlights } = get();
    return spotlights.find((s) => s.spotlightType === id);
  },

  getSpotlightById: async (id) => {
    const { spotlights } = get();
    let spotlight = spotlights.find((s) => s.id === id);
    if (spotlight) {
      return spotlight;
    }
    spotlight = await fetchSpotlightById(id);
    return spotlight;
  },

  // Get the 'feature' spotlight
  getFeatureSpotlight: () => {
    const { spotlights } = get();
    return spotlights.find((s) => s.spotlightType === 'feature');
  },

  // Clear all spotlights and dismissed state
  clear: () => {
    set({ spotlights: [], dismissed: [], error: null, lastFetched: null });
  },
}));
